import { Button, Stack, vars, Text, textVariants } from '@etg/wings';
import { useProperty, useTranslation } from '@eti/providers';
import { css } from '@eti/styles';
import type { MouseEvent } from 'react';
import type { IncludedCheckedBaggage } from '@eti/schema-types';
import {
  getMinNumberOfSeats,
  shouldShowSeatsLeftNotification,
} from '../../../../utils/resultUtils';
import SeatsLeftInfo from '../../../../../../common/components/SeatsLeftInfo';
import type {
  AvailableExtraProductsType,
  BoundsType,
  IncludedCabinBaggageType,
} from '../../../../models';
import type {
  IncludedCabinBaggageType as RebookingTripIncludedCabinBaggageType,
  RebookingTripBoundsType,
} from '../../../../../change-trip/models';
import type { RebookingTicketProps, Ticket } from '../../drawer/model';
import { routes } from '../../../../../../constants/routesAndApisConstants';
import { tripHasSelfTransfer } from '../../utils/trip';
import { TripBaggageInfo } from './TripBaggageInfo';
import PriceSection from './PriceSection';

const tripBaggageAndPriceInfoWrapperStyles = css`
  height: 100%;
  justify-content: space-between;
  padding: 8px 16px;
`;

const fixedWidthStyles = css`
  margin-top: 8px;
  width: 178px;
`;

const selfTransferPromotionStyles = css`
  color: ${vars.colors.promotional.foreground};
  text-align: right;
`;

export interface TripBaggageAndPriceInfoDesktopProps {
  availableExtraProducts?: AvailableExtraProductsType;
  bounds: BoundsType | RebookingTripBoundsType;
  includedCabinBaggage: IncludedCabinBaggageType | RebookingTripIncludedCabinBaggageType;
  includedCheckedBaggage?: IncludedCheckedBaggage | null;
  isVI?: boolean;
  numberOfTravelers?: number;
  onButtonClick: (e: MouseEvent<HTMLElement>) => void;
  ticket: RebookingTicketProps | Ticket;
}

const TripBaggageAndPriceInfoDesktop = ({
  availableExtraProducts = [],
  bounds,
  includedCabinBaggage,
  includedCheckedBaggage,
  isVI,
  numberOfTravelers,
  onButtonClick,
  ticket,
}: TripBaggageAndPriceInfoDesktopProps) => {
  const { t } = useTranslation();
  const { p } = useProperty();
  const isVISelfTransferDealEnabled: boolean = p('IbeClient.VI.SelfTransferDeal.Enabled');
  const isSelfTransferInTrip = isVISelfTransferDealEnabled && tripHasSelfTransfer(bounds);
  const numberOfSeatsLeft = getMinNumberOfSeats(bounds);
  const seatsThreshold = p('Air.Result.ShowNumberOfSeatsLeft.WhenLessThan');
  const isChangeTripPage = window.location.pathname.includes(routes.CHANGE_TRIP);
  const shouldShowSeatsLeftNotificationMessage =
    !isChangeTripPage &&
    shouldShowSeatsLeftNotification(numberOfSeatsLeft, numberOfTravelers, seatsThreshold);
  const shouldRenderSeatsLeftInfo = shouldShowSeatsLeftNotificationMessage && !isSelfTransferInTrip;

  return (
    <Stack align="end" className={tripBaggageAndPriceInfoWrapperStyles}>
      <TripBaggageInfo
        availableExtraProducts={availableExtraProducts}
        bounds={bounds}
        includedCabinBaggage={includedCabinBaggage}
        includedCheckedBaggage={includedCheckedBaggage}
        isVI={isVI}
      />
      <Stack align="end" className={fixedWidthStyles} spacing={8}>
        {isSelfTransferInTrip && (
          <Text className={selfTransferPromotionStyles} variant={textVariants._14_BOLD}>
            {t('Result.Page.SelfTransferDeals.Promotion.Text')}
          </Text>
        )}

        {shouldRenderSeatsLeftInfo && <SeatsLeftInfo numberOfSeatsLeft={numberOfSeatsLeft} />}

        <PriceSection
          priceDisplayText={ticket.priceDisplayText}
          pricePerPerson={ticket.pricePerPerson}
        />

        <Button
          className={fixedWidthStyles}
          data-testid="view-trip-button"
          onClick={onButtonClick}
          variant="primary"
        >
          {t('Result.Page.TripCard.Button.Text')}
        </Button>
      </Stack>
    </Stack>
  );
};

export default TripBaggageAndPriceInfoDesktop;
